import React, { useState, useEffect, useRef } from 'react';
import {
  TextField,
  Paper,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  CircularProgress,
  Box
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

const LocationSearch = ({ value, onChange, placeholder = "Rechercher une ville, région..." }) => {
  const [query, setQuery] = useState(value || '');
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debounceRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    setQuery(value || '');
  }, [value]);

  useEffect(() => {
    // Close suggestions when clicking outside
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchLocations = async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setSuggestions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Using Nominatim API from OpenStreetMap
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(searchQuery)}&` +
        `countrycodes=fr&` +
        `format=json&` +
        `limit=8&` +
        `addressdetails=1&` +
        `extratags=1`
      );

      if (response.ok) {
        const data = await response.json();
        
        // Filter and format results
        const formattedSuggestions = data
          .filter(item => 
            item.address && 
            (item.address.city || item.address.town || item.address.village || item.address.municipality)
          )
          .map(item => {
            const address = item.address;
            const city = address.city || address.town || address.village || address.municipality;
            const state = address.state;
            const postcode = address.postcode;
            
            return {
              id: item.place_id,
              display_name: item.display_name,
              city: city,
              state: state,
              postcode: postcode,
              formatted: `${city}${postcode ? ` (${postcode})` : ''}${state ? `, ${state}` : ''}`,
              lat: parseFloat(item.lat),
              lon: parseFloat(item.lon)
            };
          })
          // Remove duplicates based on city name
          .filter((item, index, self) => 
            index === self.findIndex(t => t.city === item.city && t.state === item.state)
          );

        setSuggestions(formattedSuggestions);
      }
    } catch (error) {
      console.error('Error searching locations:', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (event) => {
    const newQuery = event.target.value;
    setQuery(newQuery);
    setShowSuggestions(true);

    // Clear previous debounce
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Debounce the search
    debounceRef.current = setTimeout(() => {
      searchLocations(newQuery);
    }, 300);
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion.formatted);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Call the onChange callback with the selected location
    if (onChange) {
      onChange({
        query: suggestion.formatted,
        city: suggestion.city,
        state: suggestion.state,
        postcode: suggestion.postcode,
        coordinates: {
          lat: suggestion.lat,
          lon: suggestion.lon
        }
      });
    }
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <Box ref={containerRef} sx={{ position: 'relative', width: '100%' }}>
      <TextField
        fullWidth
        placeholder={placeholder}
        value={query}
        onChange={handleInputChange}
        onFocus={handleFocus}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          endAdornment: loading && (
            <InputAdornment position="end">
              <CircularProgress size={20} />
            </InputAdornment>
          ),
        }}
      />
      
      {showSuggestions && suggestions.length > 0 && (
        <Paper
          elevation={4}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000,
            maxHeight: 300,
            overflow: 'auto',
            mt: 0.5
          }}
        >
          <List dense>
            {suggestions.map((suggestion) => (
              <ListItem
                key={suggestion.id}
                button
                onClick={() => handleSuggestionClick(suggestion)}
                sx={{
                  '&:hover': {
                    backgroundColor: '#f5f5f5'
                  }
                }}
              >
                <ListItemText
                  primary={suggestion.city}
                  secondary={`${suggestion.postcode ? suggestion.postcode + ', ' : ''}${suggestion.state}`}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default LocationSearch;